// Setup › Workforce › Chief of Staff (`/c/{slug}/setup/chief-of-staff`; COS-SLICE1,
// docs/design/2026-10-28-chief-of-staff-and-assistants.md §2.5, §9.1): one small form for the
// Chief of Staff's loop — how often it checks, its hourly and daily run caps, quiet hours, how
// long an approval may wait, how many effects one run may make, follow-up cards on or off — and
// each DE's digest (on or off, the channel it posts in) with its recent use, read-only. One
// `GET`, one `PUT` (`chiefOfStaff.*`). Managers only (the Setup gate); with the `agents` add-on
// off the page is the add-on banner alone (no call).

import type { OperationOutput } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SaveIndicator,
  SettingsSection,
  Switch,
  workerName,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BotIcon, ClockIcon, MailIcon, NewspaperIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { AgentsOffBanner } from "../agents/agents-ux";
import { BriefSettingsRow } from "../briefs/brief-settings-modal";
import type { CompanyAccess } from "../company";
import { LoadError, Loading } from "../screen-parts";
import { useFeature } from "../use-feature";
import { CosModeSection, SuggestTeamBanner } from "./cos-mode";
import { problemText } from "./problem-text";
import { SetupPage } from "./setup-page";

type CosView = OperationOutput<"chiefOfStaff.get">;
type CosSettings = CosView["settings"];
type DigestDe = CosView["des"][number];

export const cosKey = (co: string) => ["company", co, "chief-of-staff"] as const;
export const cosQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: cosKey(co),
    queryFn: () => api.client.chiefOfStaff.get({ params: { co } }),
  });

type Draft = {
  interval_min: string;
  max_runs_per_hour: string;
  max_runs_per_day: string;
  approval_wait_hours: string;
  max_effects_per_run: string;
  quiet: boolean;
  quiet_from: string;
  quiet_to: string;
  quiet_weekends: boolean;
  suggestions: boolean;
  des: Record<string, { enabled: boolean; channel_id: string }>;
};

const draftOf = (v: CosView): Draft => ({
  interval_min: String(v.settings.interval_min),
  max_runs_per_hour: String(v.settings.max_runs_per_hour),
  max_runs_per_day: String(v.settings.max_runs_per_day),
  approval_wait_hours: String(v.settings.approval_wait_hours),
  max_effects_per_run: String(v.settings.max_effects_per_run),
  quiet: v.settings.quiet_hours !== null,
  quiet_from: v.settings.quiet_hours?.from ?? "19:00",
  quiet_to: v.settings.quiet_hours?.to ?? "08:00",
  quiet_weekends: v.settings.quiet_hours?.weekends ?? true,
  suggestions: v.settings.suggestions,
  des: Object.fromEntries(
    v.des.map((d) => [d.membership_id, { enabled: d.enabled, channel_id: d.channel_id ?? "" }]),
  ),
});

/** Each number field's range (the contract's `CosSettings`). */
const RANGES = {
  interval_min: [5, 60],
  max_runs_per_hour: [1, 60],
  max_runs_per_day: [1, 500],
  approval_wait_hours: [1, 72],
  max_effects_per_run: [1, 100],
} as const;
type NumberKey = keyof typeof RANGES;

const intIn = (v: string, [lo, hi]: readonly [number, number]) => {
  const n = Number(v);
  return v.trim() !== "" && Number.isInteger(n) && n >= lo && n <= hi ? n : null;
};

/** The PUT body, or null while a field is out of range. */
function bodyOf(d: Draft, des: readonly DigestDe[]) {
  const nums = {} as Record<NumberKey, number>;
  for (const k of Object.keys(RANGES) as NumberKey[]) {
    const n = intIn(d[k], RANGES[k]);
    if (n === null) return null;
    nums[k] = n;
  }
  const settings: CosSettings = {
    ...nums,
    quiet_hours: d.quiet
      ? { from: d.quiet_from, to: d.quiet_to, weekends: d.quiet_weekends }
      : null,
    suggestions: d.suggestions,
  };
  return {
    settings,
    des: des.map((x) => {
      const row = d.des[x.membership_id] ?? { enabled: x.enabled, channel_id: x.channel_id ?? "" };
      return {
        membership_id: x.membership_id,
        enabled: row.enabled,
        channel_id: row.channel_id || null,
      };
    }),
  };
}

export function ChiefOfStaffScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const agents = useFeature("agents");
  const q = useQuery({ ...cosQuery(api, company.id), enabled: agents.enabled });
  return (
    <SetupPage title="Chief of Staff" icon={BotIcon} slot="setup-chief-of-staff">
      {!agents.enabled ? (
        <AgentsOffBanner company={company} className="" />
      ) : q.isPending ? (
        <Loading label="Loading the Chief of Staff" rows={6} />
      ) : q.isError ? (
        <LoadError what="The Chief of Staff" error={q.error} />
      ) : (
        <div className="flex flex-col gap-6">
          {/* COS-MODES: Solo with heads suggests Team (one click; never automatic), then the
              mode's own form (kept outside the loop's form below). */}
          <SuggestTeamBanner api={api} co={company.id} />
          <CosModeSection api={api} co={company.id} />
          {/* DE-BRIEFS: the company's brief rhythm and the CEO report, in their own modal (kept
              outside the form below: a modal's submit bubbles through the React tree). */}
          <SettingsSection id="briefs" icon={MailIcon} title="Briefs">
            <BriefSettingsRow
              api={api}
              co={company.id}
              target={{ scope: "company" }}
              label="Briefs and CEO report"
            />
          </SettingsSection>
          {/* One form per DE set; a save keeps the form (and its "Saved"). */}
          <CosForm
            key={q.data.des.map((d) => d.membership_id).join(",")}
            api={api}
            co={company.id}
            view={q.data}
          />
        </div>
      )}
    </SetupPage>
  );
}

/** A label with its (i) beside it (the explanation lives behind the icon, UX-A). */
function LabelWithTip({
  htmlFor,
  children,
  tip,
}: {
  htmlFor: string;
  children: ReactNode;
  tip?: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <FieldLabel htmlFor={htmlFor}>{children}</FieldLabel>
      {tip ? <InfoTip label={`About ${String(children)}`}>{tip}</InfoTip> : null}
    </div>
  );
}

function CosForm({ api, co, view }: { api: Api; co: string; view: CosView }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => draftOf(view));
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const id = useId();
  const save = useMutation({
    mutationFn: (body: NonNullable<ReturnType<typeof bodyOf>>) =>
      api.client.chiefOfStaff.put({ params: { co }, body }),
    onSuccess: (v) => {
      setDraft(draftOf(v));
      setSavedAt(Date.now());
      qc.setQueryData(cosKey(co), v);
    },
  });
  const body = bodyOf(draft, view.des);
  const dirty = JSON.stringify(draft) !== JSON.stringify(draftOf(view));
  const set = (patch: Partial<Draft>) => {
    save.reset();
    setDraft((d) => ({ ...d, ...patch }));
  };
  const setDe = (membership: string, patch: Partial<Draft["des"][string]>) =>
    setDraft((d) => {
      const cur = d.des[membership] ?? { enabled: false, channel_id: "" };
      return { ...d, des: { ...d.des, [membership]: { ...cur, ...patch } } };
    });
  const num = (key: NumberKey, label: string, tip?: string) => {
    const [lo, hi] = RANGES[key];
    return (
      <Field>
        <LabelWithTip htmlFor={`${id}-${key}`} {...(tip ? { tip } : {})}>
          {label}
        </LabelWithTip>
        <Input
          id={`${id}-${key}`}
          type="number"
          inputMode="numeric"
          min={lo}
          max={hi}
          step={1}
          className="h-10"
          value={draft[key]}
          aria-invalid={intIn(draft[key], RANGES[key]) === null}
          onChange={(e) => set({ [key]: e.target.value } as Partial<Draft>)}
        />
      </Field>
    );
  };
  const interval =
    intIn(draft.interval_min, RANGES.interval_min) ?? Number(view.settings.interval_min);
  return (
    <form
      aria-label="Chief of Staff"
      className="flex flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (body) save.mutate(body);
      }}
    >
      <SettingsSection id="loop" icon={ClockIcon} title="Loop">
        <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1">
          {num(
            "interval_min",
            "Check every (min)",
            `Checks every ${interval} minutes; wakes the Chief of Staff only when something needs it.`,
          )}
          {num("max_runs_per_hour", "Runs per hour")}
          {num("max_runs_per_day", "Runs per day")}
          {num("approval_wait_hours", "Approval wait (hours)")}
          {num(
            "max_effects_per_run",
            "Effects per run",
            "Task creations and handoffs one agent run may make.",
          )}
        </div>
        <div className="mt-5 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id={`${id}-quiet`}
              checked={draft.quiet}
              onCheckedChange={(quiet) => set({ quiet })}
            />
            <Label htmlFor={`${id}-quiet`}>Quiet hours</Label>
            <InfoTip label="About Quiet hours">
              Only urgent items wake it: a new approval, a missed deadline, a task bounced three
              times.
            </InfoTip>
          </div>
          {draft.quiet ? (
            <div className="flex flex-wrap items-end gap-4" data-slot="quiet-hours">
              <Field className="w-32">
                <FieldLabel htmlFor={`${id}-from`}>From</FieldLabel>
                <Input
                  id={`${id}-from`}
                  type="time"
                  className="h-10"
                  required
                  value={draft.quiet_from}
                  onChange={(e) => set({ quiet_from: e.target.value })}
                />
              </Field>
              <Field className="w-32">
                <FieldLabel htmlFor={`${id}-to`}>To</FieldLabel>
                <Input
                  id={`${id}-to`}
                  type="time"
                  className="h-10"
                  required
                  value={draft.quiet_to}
                  onChange={(e) => set({ quiet_to: e.target.value })}
                />
              </Field>
              <div className="flex h-10 items-center gap-2">
                <Checkbox
                  id={`${id}-weekends`}
                  checked={draft.quiet_weekends}
                  onCheckedChange={(on) => set({ quiet_weekends: on === true })}
                />
                <Label htmlFor={`${id}-weekends`} className="font-normal">
                  Weekends
                </Label>
              </div>
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <Switch
              id={`${id}-cards`}
              checked={draft.suggestions}
              onCheckedChange={(suggestions) => set({ suggestions })}
            />
            <Label htmlFor={`${id}-cards`}>Follow-up cards</Label>
            <InfoTip label="About Follow-up cards">
              "Make this a task?" under chat messages with a date. Nothing is created until a person
              confirms.
            </InfoTip>
          </div>
        </div>
      </SettingsSection>
      <SettingsSection id="digest" icon={NewspaperIcon} title="Digest">
        {view.des.length === 0 ? (
          <EmptyState variant="card" icon={BotIcon} title="No digital employees yet" />
        ) : (
          <ul className="flex flex-col divide-y divide-border-subtle" data-slot="cos-des">
            {view.des.map((de) => (
              <DigestRow
                key={de.membership_id}
                de={de}
                row={draft.des[de.membership_id] ?? { enabled: false, channel_id: "" }}
                onChange={(patch) => setDe(de.membership_id, patch)}
              />
            ))}
          </ul>
        )}
      </SettingsSection>
      {save.error ? (
        <Notice tone="danger" role="alert">
          {problemText(save.error, "The settings could not be saved.")}
        </Notice>
      ) : null}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!dirty || !body || save.isPending}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <SaveIndicator savedAt={savedAt} />
      </div>
    </form>
  );
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : "Never");

function DigestRow({
  de,
  row,
  onChange,
}: {
  de: DigestDe;
  row: { enabled: boolean; channel_id: string };
  onChange: (patch: Partial<{ enabled: boolean; channel_id: string }>) => void;
}) {
  const id = useId();
  const name = workerName(de.name);
  return (
    <li
      data-de={de.membership_id}
      className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3 first:pt-0 last:pb-0"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{name}</div>
        <div className="text-xs text-muted-foreground">
          Last digest: {when(de.last_digest_at)} · Runs: {de.runs_last_hour} this hour ·{" "}
          {de.runs_last_day} today
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          id={`${id}-on`}
          checked={row.enabled}
          onCheckedChange={(enabled) => onChange({ enabled })}
        />
        <Label htmlFor={`${id}-on`} className="font-normal">
          Digest<span className="sr-only"> for {name}</span>
        </Label>
      </div>
      <NativeSelect
        aria-label={`Channel for ${name}`}
        className="w-48"
        value={row.channel_id}
        onChange={(e) => onChange({ channel_id: e.target.value })}
      >
        <NativeSelectOption value="">First open channel</NativeSelectOption>
        {de.channels.map((c) => (
          <NativeSelectOption key={c.id} value={c.id}>
            {`#${c.name}`}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </li>
  );
}
